import React, { Dispatch, SetStateAction, useMemo, useRef } from "react";
import { Button, Icon, SearchBar } from "../../../components/tailwind";
import { Fieldset, Field } from "./types";

type Props = {
  defaultFieldset: Fieldset;
  fieldsets: Fieldset[];
  selectedFieldset: Fieldset | undefined;
  setSelectedFieldset: (fieldset: Fieldset) => void;
  setIsCopying: Dispatch<SetStateAction<boolean>>;
  isCopying: boolean;
  editModeField?: boolean;
  fields?: Field[];
  selectedField?: Field;
  setSelectedField?: (field: Field) => void;
  search: string;
  setSearch: (value: string) => void;
};

const matchesSearch = (value: string, search: string): boolean =>
  !search || value.toLowerCase().includes(search.toLowerCase());

const fieldKey = (field: Field): string =>
  field.key ?? `section-${field.section_id}`;

const fieldDisplayName = (field: Field): string =>
  field.name ?? field.key ?? "";

const SCROLL_STYLE = {
  maxHeight: "calc(100vh - 220px)",
  scrollbarGutter: "stable",
} as const;

const LIST_CLASSES = "tw-flex tw-flex-col tw-gap-1 tw-overflow-auto tw-pr-1";
const ROW_CLASSES =
  "tw-flex tw-items-start tw-gap-2 tw-cursor-pointer tw-rounded-lg tw-px-3 tw-py-2 tw-text-blue-500 tw-font-semibold tw-transition-all hover:tw-bg-gray-100 aria-selected:tw-bg-gray-100 aria-selected:tw-font-bold";
const NAME_CLASSES = "tw-flex-1 tw-min-w-0 tw-break-words";

export const ListFieldsets: React.FC<Props> = ({
  defaultFieldset,
  fieldsets,
  selectedFieldset,
  setSelectedFieldset,
  setIsCopying,
  isCopying,
  editModeField,
  fields = [],
  selectedField,
  setSelectedField,
  search,
  setSearch,
}) => {
  const clickedCopy = useRef(false);

  const filteredFields = useMemo(
    () => fields.filter((f) => matchesSearch(fieldDisplayName(f), search)),
    [fields, search]
  );

  const filteredFieldsets = useMemo(
    () => fieldsets.filter((fs) => matchesSearch(fs.name, search)),
    [fieldsets, search]
  );

  const showDefaultFieldset = matchesSearch(defaultFieldset.name, search);

  const placeholder = editModeField ? "Search fields" : "Search schemas";

  if (editModeField && setSelectedField) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-2">
        <SearchBar
          value={search}
          onValueChange={setSearch}
          placeholder={placeholder}
          data-testid="list-fieldsets-search"
        />
        <ul
          data-testid="list-fieldsets"
          className={LIST_CLASSES}
          style={SCROLL_STYLE}
        >
          {filteredFields.length === 0 && (
            <li
              className="tw-px-3 tw-py-2 tw-text-sm tw-text-gray-500"
              data-testid="list-fieldsets-empty"
            >
              No fields found
            </li>
          )}
          {filteredFields.map((field) => {
            const isSelected =
              (field.key && selectedField?.key === field.key) ||
              (field.section_id != null &&
                selectedField?.section_id === field.section_id);
            return (
              <li
                key={fieldKey(field)}
                aria-selected={isSelected ? "true" : "false"}
                data-testid={`select-field-pim-${fieldDisplayName(field)}`}
                onClick={(): void => setSelectedField(field)}
                className={ROW_CLASSES}
              >
                <span className={NAME_CLASSES}>
                  {fieldDisplayName(field)}
                  {field.key && field.name && field.name !== field.key && (
                    <span
                      className="tw-block tw-text-xs tw-text-gray-400 tw-font-normal tw-break-all"
                      data-testid={`select-field-pim-key-${field.key}`}
                    >
                      {field.key}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const showEmpty = !showDefaultFieldset && filteredFieldsets.length === 0;

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <SearchBar
        value={search}
        onValueChange={setSearch}
        placeholder={placeholder}
        data-testid="list-fieldsets-search"
      />
      <ul
        data-testid="list-fieldsets"
        className={LIST_CLASSES}
        style={SCROLL_STYLE}
      >
        {showEmpty && (
          <li
            className="tw-px-3 tw-py-2 tw-text-sm tw-text-gray-500"
            data-testid="list-fieldsets-empty"
          >
            No schemas found
          </li>
        )}
        {[
          ...(showDefaultFieldset ? [defaultFieldset] : []),
          ...filteredFieldsets,
        ].map((fieldset) => {
          const isSelected = fieldset.id === selectedFieldset?.id;
          return (
            <li
              key={fieldset.id}
              aria-selected={isSelected ? "true" : "false"}
              data-testid={`select-fieldset-update-${fieldset.name}`}
              onClick={(): void => {
                if (isCopying && !clickedCopy.current) {
                  setIsCopying(false);
                }
                clickedCopy.current = false;
                setSelectedFieldset(fieldset);
              }}
              className={ROW_CLASSES}
            >
              <span className={NAME_CLASSES}>{fieldset.name}</span>
              <span className="tw-ml-auto tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                {fieldset.use_as_default && (
                  <span
                    data-balloon="Default Input schema"
                    data-balloon-pos="left"
                  >
                    <Icon
                      name="check"
                      data-testid="default-fieldset"
                      className="tw-text-textual-blue"
                    />
                  </span>
                )}
                <span data-balloon="Copy schema" data-balloon-pos="left">
                  <Button
                    data-testid={`select-fieldset-copy-${fieldset.name}`}
                    variant="secondary-alt"
                    icon
                    size="sm"
                    compact
                    onClick={(event): void => {
                      event.stopPropagation();
                      clickedCopy.current = true;
                      setIsCopying(true);
                    }}
                  >
                    <Icon name="content_copy" />
                  </Button>
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
